import { useCallback, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { Row } from '@guestyci/foundation/Layout';
import TextField from '@guestyci/foundation/TextField';
import IconButton from '@guestyci/foundation/IconButton';
import StatusPill from '@guestyci/foundation/StatusPill';
import { Menu, MenuItem } from '@guestyci/foundation/Menu';
import Tooltip from '@guestyci/foundation/Tooltip';
import { ReactComponent as IcoNotice } from '@guestyci/icons/IcoNotice.svg';
import defaultTheme from '@guestyci/foundation/theme/defaultTheme';

import tracker from 'utils/dio/dynamic-pricing';
import capitalTracker from 'utils/dio/guesty-capital';
import smartLockTracker from 'utils/dio/smart-locks';
import navigationsRoute from 'app/routes/navigationsRoute';
import diamondIconSource from 'assets/icons/diamond.svg';
import useCapitalApi from 'app/hooks/capital/useCapitalApi';
import useSmartLocksEnabled from 'app/hooks/smartLocks/useSmartLocksEnabled';
import useDPTData from 'app/hooks/dpt/useDPTEnabled';
import useDamageProtectionContainer from '../DamageProtection/useDamageProtectionContainer';
import { getDaysDifference } from 'utils/time';

function DiamondMenu() {
  const [open, setOpen] = useState(false);
  const menuAnchor = useRef<null | HTMLButtonElement>(null);
  const dptData = useDPTData();
  const isDptAvailable = dptData?.isAvailableForAddons;

  const { isDamageProtectionEnabled, isDataLoaded } =
    useDamageProtectionContainer();
  const { isCapitalEnabled } = useCapitalApi();

  const isSmartLocksEnabled = useSmartLocksEnabled();

  const isDiamondMenuVisible = [
    isDptAvailable, // can use PriceOptimizer
    isDamageProtectionEnabled && isDataLoaded, // has Damage Protection
    isSmartLocksEnabled, // don't have legacy solution OR gfhSmartLocksDiconectionflow FT is enabled and have legacy solution
    isCapitalEnabled, // Has access to Guesty Capital
  ].some(Boolean);

  const clickAddonHandler = useCallback(() => {
    setOpen(true);
    tracker.clickAddonIcon();
  }, []);

  const clickDptOptionHandler = useCallback(() => {
    setOpen(false);
    tracker.clickDptOption();
  }, []);

  const clickDpOptionHandler = useCallback(() => {
    setOpen(false);
    tracker.clickDpOption();
  }, []);

  const clickSmartLockOptionHandler = useCallback(() => {
    setOpen(false);
    smartLockTracker.clickSmartLockOption();
  }, []);

  const clickCapitalOptionHandler = useCallback(() => {
    setOpen(false);
    capitalTracker.clickGuestyCapitalDiamondOption();
  }, []);

  const dynamicPricingDaysToActivationFn = (willBeAvailableAt: any) => {
    if (!willBeAvailableAt) return 0;

    const activationDate = new Date(willBeAvailableAt);
    return Math.max(getDaysDifference(activationDate, new Date()), 0);
  };

  const dynamicPricingDaysToActivation = useMemo(() => {
    return dynamicPricingDaysToActivationFn(dptData?.willBeAvailableAt);
  }, [dptData]);

  if (!isDiamondMenuVisible) {
    return null;
  }

  return (
    <>
      <IconButton
        ref={menuAnchor}
        onClick={clickAddonHandler}
        size="lg"
        className="p-0"
        data-qa="diamond-menu-icon"
      >
        <img src={diamondIconSource} alt="access point" />
      </IconButton>
      <Menu
        anchor={menuAnchor}
        open={open}
        onClose={() => setOpen(false)}
        variant="menu"
        fitAnchor
        anchorDirection="end"
        data-qa="diamond-menu"
      >
        {isDptAvailable && (
          <>
            {dynamicPricingDaysToActivation ? (
              <MenuItem>
                <Row spacing={2}>
                  <Tooltip
                    body={`GPO will be available in ${dynamicPricingDaysToActivation} ${
                      dynamicPricingDaysToActivation === 1 ? 'day' : 'days'
                    }`}
                    placement="bottom end"
                  >
                    <Row spacing={1}>
                      <TextField bold color="light" overflowEllipsis>
                        Guesty PriceOptimizer
                      </TextField>
                      <IcoNotice width={16} fill={defaultTheme.palette.link} />
                    </Row>
                  </Tooltip>
                  <StatusPill variant="primary">Coming soon</StatusPill>
                </Row>
              </MenuItem>
            ) : (
              <MenuItem>
                <Link
                  to={navigationsRoute.DYNAMIC_PRICING}
                  onClick={clickDptOptionHandler}
                  className="no-underline"
                >
                  <Row spacing={2}>
                    <TextField bold color="dark" overflowEllipsis>
                      Guesty PriceOptimizer
                    </TextField>
                    <StatusPill variant="primary">New</StatusPill>
                  </Row>
                </Link>
              </MenuItem>
            )}
          </>
        )}
        {isDamageProtectionEnabled && isDataLoaded && (
          <MenuItem>
            <Link
              to={navigationsRoute.DAMAGE_PROTECTION}
              onClick={clickDpOptionHandler}
              className="no-underline"
            >
              <TextField bold color="dark" overflowEllipsis>
                Damage Protection
              </TextField>
            </Link>
          </MenuItem>
        )}
        {isSmartLocksEnabled && (
          <MenuItem>
            <Link
              to={navigationsRoute.SMART_LOCKS}
              onClick={clickSmartLockOptionHandler}
              className="no-underline"
            >
              <Row spacing={2}>
                <TextField bold color="dark" overflowEllipsis>
                  Guesty Locks Manager
                </TextField>
                <StatusPill variant="primary">New</StatusPill>
              </Row>
            </Link>
          </MenuItem>
        )}
        {isCapitalEnabled && (
          <MenuItem>
            <Link
              to={navigationsRoute.GUESTY_CAPITAL}
              onClick={clickCapitalOptionHandler}
              className="no-underline"
            >
              <Row spacing={2}>
                <TextField bold color="dark" overflowEllipsis>
                  Guesty Capital
                </TextField>
                <StatusPill variant="primary">New</StatusPill>
              </Row>
            </Link>
          </MenuItem>
        )}
      </Menu>
    </>
  );
}
export default DiamondMenu;
